import React from 'react';

/* Shared SVG primitives that mirror the live Hale × ToxIntel UI system.
 * No external images, no stock photography — every primitive is pure SVG
 * so it scales losslessly and serializes cleanly for export. */

export const PALETTE = {
  IVORY:  '#F5F1E8',
  SLATE:  '#0B1426',
  CORAL:  '#FF4E6A',
  MUTED:  '#5B6577',
  RULE:   'rgba(11,20,38,0.10)',
  HALE_RED:'#E63946',
  HALE_INK:'#0B0B0B',
  GREEN:  '#16624B',
  GOLD:   '#A07A14',
};

export const FONT_SERIF = '"Cormorant Garamond","Times New Roman",serif';
export const FONT_SANS  = 'Inter,system-ui,sans-serif';
export const FONT_MONO  = 'ui-monospace,SFMono-Regular,Menlo,monospace';

/** HALE red block wordmark — identity-locked, matches HaleBrand.jsx. */
export function HaleMark({ x, y, w = 110, h = 44, fontSize = 30 }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x={0} y={0} width={w} height={h} fill={PALETTE.HALE_RED} />
      <text
        x={w / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={PALETTE.HALE_INK}
        fontFamily='"Archivo Black",Impact,sans-serif'
        fontWeight={900}
        fontSize={fontSize}
        letterSpacing="1.5"
      >
        HALE
      </text>
    </g>
  );
}

/** ToxIntel serif wordmark with the superscript ᴵ — matches <TI /> in UI. */
export function ToxIntelMark({ x, y, fontSize = 28, color = PALETTE.SLATE, accent = PALETTE.CORAL }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        fill={color}
        fontFamily={FONT_SERIF}
        fontWeight={500}
        fontSize={fontSize}
        dominantBaseline="alphabetic"
      >
        Tox
        <tspan fill={accent} fontStyle="italic">Intel</tspan>
      </text>
    </g>
  );
}

/** Agyekum Group monogram — Aᴳ in serif. */
export function AgMark({ x, y, fontSize = 26, color = PALETTE.SLATE }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <text
        x={0}
        y={0}
        fill={color}
        fontFamily={FONT_SERIF}
        fontWeight={600}
        fontSize={fontSize}
        dominantBaseline="alphabetic"
      >
        A
        <tspan baselineShift="super" fontSize={fontSize * 0.55}>G</tspan>
        <tspan fontFamily={FONT_SANS} fontWeight={500} fontSize={fontSize * 0.42} fill={PALETTE.MUTED} dx={6}>
          THE AGYEKUM GROUP
        </tspan>
      </text>
    </g>
  );
}

/** Cream card with hairline border — mirrors live UI cards. */
export function Card({ x, y, w, h, fill = 'rgba(255,255,255,0.6)', stroke = PALETTE.RULE, radius = 2, children }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x={0} y={0} width={w} height={h} rx={radius} ry={radius} fill={fill} stroke={stroke} strokeWidth={1} />
      {children}
    </g>
  );
}

/** Eyebrow line (mono uppercase tracked) — matches CORAL eyebrows in UI. */
export function Eyebrow({ x, y, text, color = PALETTE.CORAL, size = 11 }) {
  return (
    <text
      x={x}
      y={y}
      fill={color}
      fontFamily={FONT_MONO}
      fontSize={size}
      letterSpacing={size * 0.25}
      style={{ textTransform: 'uppercase' }}
    >
      {String(text).toUpperCase()}
    </text>
  );
}

/** Serif headline — Cormorant Garamond, semibold. */
export function Headline({ x, y, text, size = 48, color = PALETTE.SLATE, weight = 800, anchor = 'start', italic = false }) {
  return (
    <text
      x={x}
      y={y}
      fill={color}
      fontFamily={FONT_SERIF}
      fontWeight={weight}
      fontStyle={italic ? 'italic' : 'normal'}
      fontSize={size}
      textAnchor={anchor}
    >
      {text}
    </text>
  );
}

/** Sans body — Inter. Supports multi-line via array. */
export function Body({ x, y, text, size = 14, color = PALETTE.MUTED, weight = 400, lineHeight = 1.5, maxWidth }) {
  const lines = Array.isArray(text) ? text : [text];
  return (
    <text x={x} y={y} fill={color} fontFamily={FONT_SANS} fontSize={size} fontWeight={weight}>
      {lines.map((ln, i) => (
        <tspan key={i} x={x} dy={i === 0 ? 0 : size * lineHeight}>
          {ln}
        </tspan>
      ))}
    </text>
  );
}

/** Coral pill chip — used for tags and statuses. */
export function Pill({ x, y, label, fill = PALETTE.CORAL, text = PALETTE.SLATE, padX = 10, padY = 6, size = 11 }) {
  // Approximate width based on character count — fine for the short labels we use.
  const w = Math.max(40, label.length * (size * 0.62) + padX * 2);
  const h = size + padY * 2;
  return (
    <g transform={`translate(${x},${y})`}>
      <rect x={0} y={0} width={w} height={h} rx={2} fill={fill} />
      <text
        x={w / 2}
        y={h / 2}
        textAnchor="middle"
        dominantBaseline="central"
        fill={text}
        fontFamily={FONT_MONO}
        fontSize={size}
        letterSpacing={size * 0.18}
        fontWeight={600}
        style={{ textTransform: 'uppercase' }}
      >
        {label.toUpperCase()}
      </text>
    </g>
  );
}

/** Horizontal flow arrow — coral, used between workflow nodes. */
export function FlowArrowH({ x, y, w = 60, color = PALETTE.CORAL }) {
  const head = 8;
  return (
    <g transform={`translate(${x},${y})`} stroke={color} fill={color}>
      <line x1={0} y1={0} x2={w - head} y2={0} strokeWidth={2} />
      <polygon points={`${w},0 ${w - head},${-head / 1.4} ${w - head},${head / 1.4}`} />
    </g>
  );
}

/** Vertical flow arrow — used in stacked brand journey. */
export function FlowArrowV({ x, y, h = 50, color = PALETTE.CORAL }) {
  const head = 8;
  return (
    <g transform={`translate(${x},${y})`} stroke={color} fill={color}>
      <line x1={0} y1={0} x2={0} y2={h - head} strokeWidth={2} />
      <polygon points={`0,${h} ${-head / 1.4},${h - head} ${head / 1.4},${h - head}`} />
    </g>
  );
}

/** Hairline divider rule. */
export function Rule({ x1, x2, y, color = PALETTE.RULE }) {
  return <line x1={x1} y1={y} x2={x2} y2={y} stroke={color} strokeWidth={1} />;
}

/** Check icon — used in feature bullet rows. */
export function Check({ x, y, size = 12, color = PALETTE.CORAL }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle cx={size / 2} cy={size / 2} r={size / 2} fill={color} />
      <path
        d={`M${size * 0.25},${size * 0.55} L${size * 0.45},${size * 0.72} L${size * 0.78},${size * 0.32}`}
        stroke="white"
        strokeWidth={1.6}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </g>
  );
}

/** Bullet row — check + label, evenly spaced. */
export function Bullet({ x, y, text, size = 14, color = PALETTE.SLATE, accent = PALETTE.CORAL }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <Check x={0} y={2} size={12} color={accent} />
      <text x={20} y={11} fill={color} fontFamily={FONT_SANS} fontSize={size}>
        {text}
      </text>
    </g>
  );
}

/** Brand lockup row — HALE + "Powered by ToxIntel" + "Scientific Review Partner: AG". */
export function BrandLockup({ x, y, scale = 1, showAg = true }) {
  const s = scale;
  return (
    <g transform={`translate(${x},${y}) scale(${s})`}>
      <HaleMark x={0} y={0} w={120} h={48} fontSize={32} />
      <text
        x={140}
        y={20}
        fill={PALETTE.MUTED}
        fontFamily={FONT_MONO}
        fontSize={10}
        letterSpacing={2}
        style={{ textTransform: 'uppercase' }}
      >
        POWERED BY
      </text>
      <ToxIntelMark x={140} y={42} fontSize={22} />
      {showAg && (
        <>
          <text
            x={300}
            y={20}
            fill={PALETTE.MUTED}
            fontFamily={FONT_MONO}
            fontSize={10}
            letterSpacing={2}
            style={{ textTransform: 'uppercase' }}
          >
            SCIENTIFIC REVIEW PARTNER
          </text>
          <AgMark x={300} y={42} fontSize={20} />
        </>
      )}
    </g>
  );
}

/** Generic backplate matching app's ivory/cream surface with subtle grid. */
export function Backplate({ w, h, fill = PALETTE.IVORY }) {
  return <rect x={0} y={0} width={w} height={h} fill={fill} />;
}

/** Callout — coral dot + dotted line + label. Used to annotate UI screenshots. */
export function Callout({ x, y, label, sub, anchor = 'start', dotColor = PALETTE.CORAL }) {
  return (
    <g transform={`translate(${x},${y})`}>
      <circle cx={0} cy={0} r={5} fill={dotColor} />
      <circle cx={0} cy={0} r={10} fill="none" stroke={dotColor} strokeWidth={1} opacity={0.45} />
      <text
        x={anchor === 'end' ? -18 : 18}
        y={-3}
        textAnchor={anchor}
        fill={PALETTE.SLATE}
        fontFamily={FONT_SANS}
        fontSize={14}
        fontWeight={600}
      >
        {label}
      </text>
      {sub && (
        <text
          x={anchor === 'end' ? -18 : 18}
          y={14}
          textAnchor={anchor}
          fill={PALETTE.MUTED}
          fontFamily={FONT_SANS}
          fontSize={11}
        >
          {sub}
        </text>
      )}
    </g>
  );
}
